import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Store, createSelector } from '@ngrx/store';

import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { MessagesModule } from 'primeng/messages';
import { SelectButtonModule } from 'primeng/selectbutton';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';

import { TransactionType } from 'src/generated/graphql.generated';

import { PermissionService } from '../services/permission.service';
import { LoadingState } from '../utilities/state.util';

import { BlueSageQueueActions } from './bluesage-queue.actions';
import { blueSageQueueFeature } from './bluesage-queue.feature';
import { maskEpi } from './bluesage.util';

/**
 * The BlueSage review queue — K8.
 *
 * A crew keys a sale straight into a terminal. When the middleware can tie it to
 * a job by job code, the CRM files it automatically. When it cannot — no job
 * code, a code matching nothing, or a code matching several — the payment lands
 * here, because money has moved and somebody has to say where it belongs.
 *
 * Three things about this screen are deliberate:
 *
 *  1. **Every row shows what the customer paid AND what the job is credited.**
 *     On a dual-pricing location they differ by the card differential (KC-Test
 *     runs ~3%: a $1.00 base settles $1.03). Showing only one of them means the
 *     number the customer quotes down the phone appears nowhere.
 *  2. **Attaching asks for a job, never guesses one.** The job code is a hint
 *     printed on a receipt, not a key — it is reusable and non-unique, which is
 *     exactly why these payments could not be matched automatically.
 *  3. **Dismissing requires a reason.** It is the only record of why money that
 *     really moved was written off this queue.
 *
 * Where this page lives in the app, and where its count is surfaced, is
 * MoveRight's call: the count comes from its own cheap query, so a badge can be
 * dropped anywhere without loading a single row.
 */
@Component({
    selector: 'app-bluesage-queue',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ButtonModule,
        InputTextModule,
        MessagesModule,
        SelectButtonModule,
        TableModule,
        TooltipModule,
    ],
    templateUrl: './bluesage-queue.component.html',
})
export class BlueSageQueueComponent implements OnInit {

    actions = BlueSageQueueActions;

    /** Exposed so the template names the generated enum rather than raw strings. */
    transactionType = TransactionType;

    // Every option needs a real `value`. With `value: undefined` PrimeNG falls
    // back to emitting the whole option OBJECT, which reached the query as
    // `{ label: "All" }` and 400'd on `$status: String`. `null` means "no filter".
    statusOptions = [
        { label: 'Needs review', value: 'open' },
        { label: 'Attached', value: 'attached' },
        { label: 'Dismissed', value: 'dismissed' },
        { label: 'All', value: null },
    ];

    viewModel$ = this.store.select(createSelector({
        page: blueSageQueueFeature.selectPage,
        summary: blueSageQueueFeature.selectSummary,
        loading: createSelector(
            blueSageQueueFeature.selectLoading,
            (loading) => loading === LoadingState.LOADING,
        ),
        loadFailed: blueSageQueueFeature.selectLoadFailed,
        resolvingId: blueSageQueueFeature.selectResolvingId,
        resolvingHoldId: blueSageQueueFeature.selectResolvingHoldId,
        statusFilter: blueSageQueueFeature.selectStatusFilter,
        selectedId: blueSageQueueFeature.selectSelectedId,
        jobCode: blueSageQueueFeature.selectJobCode,
        jobMatch: blueSageQueueFeature.selectJobMatch,
        lookingUp: blueSageQueueFeature.selectLookingUp,
        lookupFailed: blueSageQueueFeature.selectLookupFailed,
        dismissReason: blueSageQueueFeature.selectDismissReason,
        openCount: createSelector(
            blueSageQueueFeature.selectSummary,
            (summary) => summary.find((s) => s.status === 'open')?.payments ?? 0,
        ),
        // Nothing is attachable until a code has resolved to exactly one job.
        // The button used to enable on any non-empty text, which is how a job
        // code reached a mutation that only accepts a UUID.
        canAttach: createSelector(
            blueSageQueueFeature.selectJobMatch,
            blueSageQueueFeature.selectResolvingId,
            (match, resolving) => !resolving && Boolean(match?.job?.id),
        ),
        // A payment additionally needs the job to have one live invoice and to
        // still owe at least this much — `canTakePayment` is the server
        // answering that with the payment's own amount, not a guess made here.
        canAttachAsPayment: createSelector(
            blueSageQueueFeature.selectJobMatch,
            blueSageQueueFeature.selectResolvingId,
            (match, resolving) => !resolving && Boolean(match?.job?.canTakePayment),
        ),
        canDismiss: createSelector(
            blueSageQueueFeature.selectDismissReason,
            blueSageQueueFeature.selectResolvingId,
            (reason, resolving) => !resolving && Boolean(reason.trim()),
        ),
    }));

    /** Same reasoning as the settings panel: read the source the directive reads. */
    canRead$ = this.permissions.watchAllPermissions([ 'bluesage.query' ]);
    canResolve$ = this.permissions.watchAllPermissions([ 'bluesage.reconcile' ]);

    constructor(
        public store: Store,
        private permissions: PermissionService,
    ) { }

    ngOnInit(): void {
        this.store.dispatch(this.actions.pageOpened());
    }

    /** Minor units to a displayable amount. Never recompute money — only format it. */
    money(minorUnits: number, currency: string): string {
        return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'USD' })
            .format((minorUnits ?? 0) / 100);
    }

    /** Seconds since epoch — the platform's timestamp unit, not milliseconds. */
    when(seconds: number): Date {
        return new Date((seconds ?? 0) * 1000);
    }

    /** The device it was keyed on, in the form an operator recognises. */
    terminal(epi: string | null | undefined): string {
        return epi ? `Terminal ${ maskEpi(epi) }` : 'Unknown terminal';
    }

    card(brand: string | null | undefined, last4: string | null | undefined): string {
        if (!last4) { return ''; }
        return `${ brand || 'Card' } •${ last4 }`;
    }
}
